import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { emptyQuote } from "@/lib/quoteOptions";
import QuoteStepper from "@/components/quote/QuoteStepper";
import StepVehicle from "@/components/quote/StepVehicle";
import StepJourney from "@/components/quote/StepJourney";
import StepCollection from "@/components/quote/StepCollection";
import StepQuote from "@/components/quote/StepQuote";
import StepBooking from "@/components/quote/StepBooking";

const ORDER = ["vehicle", "journey", "collection", "quote", "booking"];

export default function Quote() {
  const [step, setStep] = useState("vehicle");
  const [data, setData] = useState(emptyQuote());
  const [preview, setPreview] = useState(null);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const next = () => {
    const i = ORDER.indexOf(step);
    if (i < ORDER.length - 1) {
      setStep(ORDER[i + 1]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const back = () => {
    const i = ORDER.indexOf(step);
    if (i > 0) {
      setStep(ORDER[i - 1]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <main data-testid="quote-page" className="min-h-screen bg-[#050505] text-white">
      {/* Slim brand header (keeps focus on the form) */}
      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors"
            data-testid="quote-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-xs tracking-[0.3em] uppercase">Back to site</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logoUrl} alt="MHJ" className="h-9 w-9 object-contain" />
            <span className="font-display font-extrabold text-sm tracking-tight hidden sm:inline">
              MHJ PRESTIGE
            </span>
          </Link>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            className="text-[#E60000] text-xs tracking-[0.2em] uppercase font-bold hover:text-white transition-colors"
            data-testid="quote-header-call"
          >
            Call {BRAND.phone}
          </a>
        </div>
      </div>

      <QuoteStepper current={step} />

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-16 pb-24">
        <AnimatePresence mode="wait">
          {step === "vehicle" && (
            <StepVehicle key="v" data={data} update={update} onNext={next} />
          )}
          {step === "journey" && (
            <StepJourney key="j" data={data} update={update} onNext={next} onBack={back} />
          )}
          {step === "collection" && (
            <StepCollection key="c" data={data} update={update} onNext={next} onBack={back} />
          )}
          {step === "quote" && (
            <StepQuote key="q" data={data} onNext={next} onBack={back} setPreview={setPreview} />
          )}
          {step === "booking" && (
            <StepBooking key="b" data={data} update={update} preview={preview} onBack={back} />
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
